import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { BookingPageContext } from "../../App";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import Pagination from "../Pagination";
import CustomSelect from "../CustomSelectField/CustomSelect";
import useServiceCategories from "../../hook/useGarage";

const AllBookingLayer = () => {
  const [records, setRecords] = useState([]);
  const [data, setData] = useState(null);
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [update, setUpdate] = useState(0);
  const { bookingPage, setBookingPage } = useContext(BookingPageContext);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [totalStats, setTotalStats] = useState({
    total_bookings: 0,
    pending_bookings: 0,
    priced_bookings: 0,
    active_bookings: 0,
    declined_bookings: 0,
    rejected_bookings: 0,
    completed_bookings: 0,
  });

  const [bookingTypeFilter, setBookingTypeFilter] = useState("");
  const totalPages = Math.ceil(total / limit);
  const { categories, loading: categoriesLoading } = useServiceCategories(
    token,
    update,
  );
  const bookingTypes = categories.map((category) => category.name);

  useEffect(() => {
    const fetchAllBookings = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams({
          page: bookingPage,
          status: statusFilter,
          booking_type: bookingTypeFilter,
          limit: limit,
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/all-bookings?${searchParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          const sortedData = json.data.bookings.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setRecords(sortedData);
          setData(sortedData);
          setTotal(json.data.pagination.total_count);
          setHasNext(json.data.pagination.has_next);
          setHasPrev(json.data.pagination.has_prev);
          setLimit(json.data.pagination.limit);
          setLastPage(json.data.pagination.current_page);
          setTotalStats(json.data.total_stats);
        } else if (response.status === 401) {
          // Handle unauthorized access
          logout();
          toast.error("Your session has expired. Please log in again.");
          navigate("/sign-in");
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllBookings();
  }, [update, limit, bookingPage, statusFilter, bookingTypeFilter, token]);

  const handleView = (id) => {
    navigate("/booking-details/" + id);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "pending":
        return "bg-warning-focus text-warning-600 border-warning-main";
      case "priced":
        return "bg-purple-50 text-purple-600 !border-purple-main";
      case "active":
        return "bg-success-focus text-success-600 border-success-main";
      case "declined":
        return "bg-danger-focus text-danger-600 border-danger-main";
      case "rejected":
        return "bg-danger-focus text-danger-600 border-danger-main";
      case "cancelled":
        return "bg-neutral-200 text-neutral-600 border-neutral-400";
      case "completed":
        return "bg-info-focus text-info-600 border-info-main";
      default:
        return "bg-neutral-200 text-neutral-600 border-neutral-400";
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-end flex-wrap gap-3 justify-content-end">
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* <CustomSelect
            value={bookingTypeFilter}
            onChange={(e) => {
              setBookingTypeFilter(e.target.value);
              setBookingPage(0);
            }}
            options={[
              { value: "", label: "All Booking Types" },
              ...bookingTypes.map((type) => ({
                value: type,
                label: type,
              })),
            ]}
          /> */}
          <CustomSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setBookingPage(0);
            }}
            options={[
              { value: "", label: "All Status" },
              { value: "pending", label: "Pending" },
              { value: "priced", label: "Priced" },
              { value: "active", label: "Active" },
              { value: "declined", label: "Declined" },
              { value: "rejected", label: "Rejected" },
              { value: "cancelled", label: "Cancelled" },
              { value: "completed", label: "Completed" },
            ]}
          />
        </div>
      </div>

      {/* Stats Overview */}
      <div className="card-body p-24 border-bottom">
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-primary-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-primary-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:calendar-check" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Total
                  </span>
                  <span className="text-lg fw-bold text-primary-600">
                    {totalStats.total_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-warning-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-warning-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:clock-outline" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Pending
                  </span>
                  <span className="text-lg fw-bold text-warning-600">
                    {totalStats.pending_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-purple-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-purple-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:currency-usd" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Priced
                  </span>
                  <span className="text-lg fw-bold text-purple-600">
                    {totalStats.priced_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-success-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-success-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:progress-check" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Active
                  </span>
                  <span className="text-lg fw-bold text-success-600">
                    {totalStats.active_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-danger-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-danger-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:close-circle-outline" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Declined
                  </span>
                  <span className="text-lg fw-bold text-danger-600">
                    {totalStats.declined_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-danger-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-danger-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:cancel" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Rejected
                  </span>
                  <span className="text-lg fw-bold text-danger-600">
                    {totalStats.rejected_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl">
            <div className="card bg-info-50 border-0 p-16 radius-8">
              <div className="d-flex align-items-center">
                <div className="bg-info-600 text-white w-40-px h-40-px d-flex align-items-center justify-content-center rounded-circle me-12 flex-shrink-0">
                  <Icon icon="mdi:check-all" className="fs-20" />
                </div>
                <div>
                  <span className="text-md fw-medium text-secondary-light d-block">
                    Completed
                  </span>
                  <span className="text-lg fw-bold text-info-600">
                    {totalStats.completed_bookings}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                {/* <th scope="col">ID</th> */}
                <th scope="col">Customer</th>
                <th scope="col">Garage</th>
                <th scope="col">Vehicle</th>
                {/* <th scope="col">Booking Type</th> */}
                <th scope="col">Services</th>
                <th scope="col">Booking Date</th>
                <th scope="col">Status</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No bookings found
                  </td>
                </tr>
              ) : (
                records.map((booking) => (
                  <tr
                    key={booking.id}
                    onClick={() => handleView(booking.id)}
                    className="cursor-pointer"
                  >
                    {/* <td>{booking.id}</td> */}
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={
                            booking.user.image ||
                            "assets/images/user-list/user-list1.png"
                          }
                          alt="Customer"
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                        />
                        <div>
                          <span className="text-md mb-0 fw-normal text-secondary-light d-block">
                            {booking.user.fullName}
                          </span>
                          {booking.user.email && (
                            <span className="text-sm text-secondary-light">
                              {booking.user.email || "-"}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {booking.garage.garage_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {booking.vehicle.model} ({booking.vehicle.licensePlate})
                      </span>
                    </td>
                    {/* <td className="text-capitalize">
                      {booking.booking_type || "-"}
                    </td> */}
                    <td style={{ width: "fit-content", maxWidth: "400px" }}>
                      {booking.needs_emergency_service ? (
                        <span className="badge bg-danger-100 text-danger-600 px-8 py-4 radius-4 text-xs">
                          Emergency
                        </span>
                      ) : (booking.requested_services || []).length > 0 ? (
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          {booking.requested_services
                            .slice(0, 2)
                            .map((service, index) => (
                              <span
                                key={index}
                                className="badge bg-primary-100 text-primary-600 px-8 py-4 radius-4 text-xs"
                                style={{
                                  width: "fit-content",
                                  maxWidth: "250px",
                                  whiteSpace: "normal",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {typeof service === "string"
                                  ? service
                                  : service.name}
                              </span>
                            ))}

                          {booking.requested_services.length > 2 && (
                            <span
                              className="badge bg-neutral-200 text-neutral-600 px-8 py-4 radius-4 text-xs"
                              title={booking.requested_services
                                .slice(2)
                                .map((service) =>
                                  typeof service === "string"
                                    ? service
                                    : service.name,
                                )
                                .join(", ")}
                              style={{ cursor: "pointer" }}
                            >
                              +{booking.requested_services.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {booking.booking_date
                        ? format(new Date(booking.booking_date), "dd MMM, yyyy")
                        : "Not confirmed yet"}
                    </td>
                    <td>
                      <span
                        className={`border px-16 py-4 radius-4 fw-medium text-sm d-inline-block text-center ${getStatusClass(booking.status)}`}
                        style={{ width: "130px" }}
                      >
                        {booking.status.charAt(0).toUpperCase() +
                          booking.status.slice(1)}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(booking.id);
                          }}
                          className="bg-info-focus bg-hover-primary-200 text-primary-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="View Details"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <div className="d-flex align-items-center gap-2">
            <span>
              Showing {records.length} of {total} entries
            </span>
            <CustomSelect
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setBookingPage(0); // Reset to first page when changing limit
              }}
              options={[
                { value: "10", label: "10" },
                { value: "15", label: "15" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
          </div>

          <Pagination
            currentPage={bookingPage}
            totalPages={totalPages}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={setBookingPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AllBookingLayer;
